<template>
  <v-dialog :value="true" max-width="520" persistent scrollable>
    <v-card>
      <v-toolbar flat dense color="primary" dark>
        <v-icon small class="mr-2">fab fa-whatsapp</v-icon>
        <v-toolbar-title class="subtitle-1">
          {{ $t('start_whatsapp_chat') }}
        </v-toolbar-title>
      </v-toolbar>

      <v-card-text class="pt-4">
        <p class="body-2 mb-4">{{ $t('start_whatsapp_hint') }}</p>

        <v-skeleton-loader v-if="loading" type="list-item-three-line" />

        <!-- Sin plantillas aprobadas no hay forma de escribir primero, y no es
             algo que se resuelva desde esta pantalla: las aprueba la
             plataforma. Conviene decirlo y no dejar el formulario vacío. -->
        <v-alert v-else-if="templates.length === 0" type="info" text dense>
          {{ $t('no_approved_templates') }}
        </v-alert>

        <template v-else>
          <v-select
            v-model="selected"
            class="mb-4"
            dense
            hide-details
            :items="options"
            :label="$t('template')"
            outlined
          />

          <v-text-field
            v-for="name in variables"
            :key="`var-${name}`"
            v-model="values[name]"
            class="mb-1"
            dense
            :label="label(name)"
            outlined
          />

          <!-- La plantilla llega con las variables sin reemplazar, así que sin
               esto no hay manera de ver qué le va a llegar al cliente hasta
               después de mandarlo, y ya se cobró. -->
          <v-card v-if="current" class="pa-3" color="grey lighten-4" flat>
            <div class="caption text--secondary mb-1">
              {{ $t('message_preview') }}
            </div>
            <div class="body-2">{{ preview }}</div>
          </v-card>
        </template>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <i-btn
          color="secondary"
          no-icon
          text
          :title="$t('cancel')"
          @click="$emit('close')"
        >
          {{ $t('cancel') }}
        </i-btn>
        <i-btn
          color="success"
          :disabled="!ready"
          :loading="sending"
          no-icon
          :title="$tc('send', 0)"
          @click="send()"
        >
          {{ $tc('send', 0) }}
        </i-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapGetters } from 'vuex'
export default {
  name: 'start-chat-dialog',
  props: {
    /**
     * Identificador del cliente al que se le va a escribir.
     */
    customer: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      loading: true,
      sending: false,
      templates: [],
      selected: null,
      values: {}
    }
  },
  computed: {
    ...mapGetters({
      company: 'company/getCompanyData'
    }),
    options() {
      return this.templates.map((item) => ({
        value: item.pk,
        text: this.translated('whatsapp_template_', item.key)
      }))
    },
    current() {
      return this.templates.find((item) => item.pk === this.selected) || null
    },
    variables() {
      return this.current && Array.isArray(this.current.variables)
        ? this.current.variables
        : []
    },
    /**
     * El cuerpo con los valores puestos, tal como lo va a leer el cliente.
     * La plataforma numera las variables por su posición, así que el nombre
     * sale de la misma lista que ordena el envío.
     */
    preview() {
      if (!this.current) {
        return ''
      }
      return (this.current.body || '').replace(
        /\{\{\s*(\d+)\s*\}\}/g,
        (coincidencia, posicion) => {
          const name = this.variables[Number(posicion) - 1]
          const value = name ? (this.values[name] || '').trim() : ''
          return value || coincidencia
        }
      )
    },
    ready() {
      if (!this.current || this.sending) {
        return false
      }
      return this.variables.every((name) =>
        Boolean((this.values[name] || '').trim())
      )
    }
  },
  watch: {
    /**
     * Cada plantilla tiene sus variables: arrastrar los valores de la
     * anterior mandaría el dato de una en el lugar de otra.
     */
    selected() {
      const values = {}
      this.variables.forEach((name) => {
        values[name] = ''
      })
      this.values = values
    }
  },
  methods: {
    /**
     * Nombre legible de una plantilla o de una de sus variables. Las claves
     * son del proveedor y están en inglés, y la imprenta puede tener alguna
     * que no esté en el juego estándar: si no hay traducción se muestra la
     * clave, que es preferible a una etiqueta vacía.
     * @param {String} prefix - Prefijo de la clave de traducción.
     * @param {String} name - Clave del proveedor.
     * @returns {String} Etiqueta a mostrar.
     */
    translated(prefix, name) {
      const key = `${prefix}${name}`
      return this.$te(key) ? this.$t(key) : name
    },
    label(name) {
      return this.translated('whatsapp_field_', name)
    },
    async load() {
      try {
        const response = await this.$api.whatsapp.template.list({
          opt: {
            params: { company_id: this.company.pk },
            skipErrorToast: true
          }
        })
        const datos = response.data
        const resultados = datos.results !== undefined ? datos.results : datos

        // Sólo sirven las aprobadas y activas: el resto las rechaza la
        // plataforma en el momento del envío.
        this.templates = resultados.filter(
          (item) => item.status === 'approved' && item.active
        )

        if (this.templates.length > 0) {
          this.selected = this.templates[0].pk
        }
      } catch (e) {
        this.templates = []
      } finally {
        this.loading = false
      }
    },
    async send() {
      this.sending = true

      try {
        const response = await this.$api.whatsapp.start({
          form: {
            customer: this.customer,
            template: this.selected,
            parameters: this.values
          },
          opt: {
            params: { company_id: this.company.pk },
            skipErrorToast: true
          }
        })
        this.$toast.success(this.$t('whatsapp_chat_started'))
        this.$emit('started', response.data)
      } catch (e) {
        const datos = e.response ? e.response.data : null
        const motivo =
          datos &&
          (datos.detail || datos.customer || datos.template || datos.parameters)

        this.$toast.error(
          (Array.isArray(motivo) ? motivo[0] : motivo) ||
            this.$t('chat_not_started')
        )
      } finally {
        this.sending = false
      }
    }
  },
  mounted() {
    this.load()
  }
}
</script>
